import {Injectable} from '@angular/core';
import {CONSTANTS} from "../../constants";

export type MarketZone = 'EMEA' | 'NA' | 'APAC';

@Injectable({
  providedIn: 'root'
})
export class DateService {

  readonly MONTH_NAMES = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  constructor() {
  }

  convertTimestampToDate(timestamp: number | string, marketZone?: MarketZone, showHour?: boolean, shortVersion?: boolean): string {
    let date!: Date;

    if (typeof timestamp === 'string') {
      if (timestamp.charAt(timestamp.length - 1) === 'Z') {
        // expected format: 2022-08-30T11:37:14.000Z
        date = new Date(timestamp);
      } else {
        date = new Date(parseInt(timestamp, 10) * 1000);
      }
    } else {
      date = new Date(timestamp * 1000);
    }

    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const amPm = hours > 11 ? 'PM' : 'AM';
    const hoursAmPm = amPm === 'PM' ? (hours - 12) : hours;

    const fillMonthByZero = (month + 1) < 10 ? '0' : '';
    const fillDayByZero = day < 10 ? '0' : '';
    const fillMinutesByZero = minutes < 10 ? '0' : '';

    switch (marketZone) {
      case "EMEA":
      case "APAC":
        // Europ: DD/MM/YYYY
        return `${fillDayByZero}${day}/${fillMonthByZero}${month + 1}/${year}`;

      case "NA":
        // US: MM/DD/YYYY
        return `${fillMonthByZero}${month + 1}/${fillDayByZero}${day}/${year}`;

      default:
        // litteral: Month. day, year
        // short version: month on 3 letters. day, year
        const dayToString: string = day.toString();
        const displayHours = showHour ? ` at ${hoursAmPm}:${fillMinutesByZero}${minutes} ${amPm}` : '';
        if (!shortVersion) {
          const ordinalDay = (day === 1 || (dayToString.length > 1 && dayToString.charAt(dayToString.length - 1) === '1')) ? 'st'
            : day === 2 ? 'nd' : day === 3 ? 'rd' : 'th';
          return `${day}<sup>${ordinalDay}</sup> ${this.MONTH_NAMES[month]} ${year}${displayHours}`;
        } else {
          return `${this.MONTH_NAMES[month].substring(0, 3)}. ${day}, ${year}`;
        }

    }
  }

  /**
   * Convert ISO string to unix timestamp in seconds
   * @param iso
   */
  convertDateToTimestamp(iso: string): number {
    return Math.round(new Date(iso).getTime() / 1000);
  }

  isLater(actualDate: string, compareDate: string): boolean {
    if (!actualDate) {
      return false;
    }
    const date1 = this.convertDateToTimestamp(actualDate);
    const date2 = this.convertDateToTimestamp(compareDate);
    return date1 > date2;
  }

  isEqual(actualDate: string, compareDate: string): boolean {
    if (!actualDate) {
      return false;
    }
    const date1 = this.convertDateToTimestamp(actualDate);
    const date2 = this.convertDateToTimestamp(compareDate);
    return date1 === date2;
  }

  isLaterOrEqual(actualDate: string, compareDate: string): boolean {
    return this.isLater(actualDate, compareDate) || this.isEqual(actualDate, compareDate);
  }

  getDayDiff(startDate: Date | string, endDate: Date | string, noSymbol?: boolean): string {
    let date1: number;
    let date2: number;
    if (typeof startDate === 'string') {
      date1 = this.convertDateToTimestamp(startDate);
    } else {
      date1 = Number(startDate);
    }
    if (typeof endDate === 'string') {
      date2 = this.convertDateToTimestamp(endDate);
    } else {
      date2 = Number(endDate);
    }

    const res = Math.round((date1 - date2) / CONSTANTS.DATE.SECONDS_IN_A_DAY);
    if (!noSymbol) {
      const symbol = res > 0 ? '+' : '';
      return symbol + res;
    } else {
      return Math.abs(res).toString();
    }
  }
}
